/**
 * API error parsing (SPEC v2 §5.0, SPEC-FRONTEND §2).
 *
 * Backend errors look like
 *   {"code": "…", "detail": "…", "errors": {"field": [{"code": "…", "message": "…"}]}, "extra": {…}}
 * `parseApiError` turns any thrown value into ready-to-show, translated texts; `useApiError`
 * applies them: field errors go to the form, everything else to a notification.
 */
import { notifications } from '@mantine/notifications';
import axios from 'axios';
import type { ReactNode } from 'react';
import { useCallback } from 'react';

import i18n from '@/i18n';

export interface ParsedApiError {
  /** API error code (`otp_invalid`, `not_found`…) or a client code: `network`, `server_error`, `unknown`, `canceled`. */
  code?: string;
  /** HTTP status, when a response was received. */
  status?: number;
  /** Human-readable message in the current UI language. */
  message: string;
  /** Translated message per field (`errors` of a `validation_error`, without `non_field_errors`). */
  fieldErrors: Record<string, string>;
  /** Raw error code per field, for callers that need to branch on it. */
  fieldCodes: Record<string, string>;
  /** `extra` payload of the error (`retry_in`, `attempts_left`, …). */
  extra: Record<string, unknown>;
}

const NON_FIELD_ERRORS = 'non_field_errors';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `code` of an API error body, if the body has one. */
export function apiErrorCode(data: unknown): string | undefined {
  return isRecord(data) && typeof data.code === 'string' ? data.code : undefined;
}

/** HTTP status of a failed request (undefined for network errors and non-HTTP errors). */
export function errorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

/** Translation of `errors:<code>`, or `undefined` when the code has no text in errors.json. */
export function translateErrorCode(code: string | undefined): string | undefined {
  if (!code) return undefined;
  const key = `errors:${code}`;
  return i18n.exists(key) ? i18n.t(key, { defaultValue: code }) : undefined;
}

function fallbackCode(status: number | undefined): string {
  if (status === undefined) return 'network';
  if (status === 401) return 'not_authenticated';
  if (status === 403) return 'permission_denied';
  if (status === 404) return 'not_found';
  if (status === 429) return 'throttled';
  if (status >= 500) return 'server_error';
  return 'unknown';
}

interface FieldIssue {
  code?: string;
  message: string;
}

/** First issue of a field: our format is `[{code, message}]`, plain DRF is `["message"]`. */
function firstIssue(value: unknown): FieldIssue | undefined {
  const item = Array.isArray(value) ? value[0] : value;
  if (typeof item === 'string') return { message: item };
  if (isRecord(item) && (typeof item.message === 'string' || typeof item.code === 'string')) {
    return {
      code: typeof item.code === 'string' ? item.code : undefined,
      message: typeof item.message === 'string' ? item.message : '',
    };
  }
  return undefined;
}

/** Flattens nested serializer errors: `{max_declared_value: {USD: [...]}}` → `max_declared_value.USD`. */
function collectFieldIssues(errors: unknown, prefix = '', out = new Map<string, FieldIssue>()) {
  if (!isRecord(errors)) return out;
  for (const [field, value] of Object.entries(errors)) {
    const path = prefix ? `${prefix}.${field}` : field;
    const issue = firstIssue(value);
    if (issue) out.set(path, issue);
    else if (isRecord(value)) collectFieldIssues(value, path, out);
  }
  return out;
}

/** Prefer our own wording for a known code; otherwise keep the server text (already localised). */
function issueText(issue: FieldIssue): string {
  return translateErrorCode(issue.code) ?? (issue.message || translateErrorCode('invalid') || '');
}

export function parseApiError(error: unknown): ParsedApiError {
  const result: ParsedApiError = { message: '', fieldErrors: {}, fieldCodes: {}, extra: {} };

  if (axios.isCancel(error)) {
    return { ...result, code: 'canceled' };
  }
  if (!axios.isAxiosError(error)) {
    return { ...result, code: 'unknown', message: translateErrorCode('unknown') ?? '' };
  }

  const status = error.response?.status;
  const body = error.response?.data;
  result.status = status;

  if (!isRecord(body)) {
    // No API error body: network failure, proxy/HTML error page, etc.
    const code = fallbackCode(status);
    return { ...result, code, message: translateErrorCode(code) ?? '' };
  }

  const detail = typeof body.detail === 'string' && body.detail ? body.detail : undefined;
  const apiCode = typeof body.code === 'string' ? body.code : undefined;
  // Our exception handler always sends `code`; plain DRF shapes (`{detail}` or `{field: [...]}`)
  // are still understood in case a response bypasses it.
  const errors = apiCode !== undefined ? body.errors : detail === undefined ? body : undefined;

  if (isRecord(body.extra)) result.extra = body.extra;

  let nonFieldMessage: string | undefined;
  for (const [field, issue] of collectFieldIssues(errors)) {
    if (field === NON_FIELD_ERRORS) {
      nonFieldMessage ??= issueText(issue);
      continue;
    }
    result.fieldErrors[field] = issueText(issue);
    if (issue.code) result.fieldCodes[field] = issue.code;
  }

  const hasFieldIssues = nonFieldMessage !== undefined || Object.keys(result.fieldErrors).length > 0;
  const code = apiCode ?? (hasFieldIssues ? 'validation_error' : fallbackCode(status));
  result.code = code;
  // Our wording for known codes first; otherwise the server text (already localised by
  // Accept-Language); generic status text as the last resort.
  result.message =
    (code === 'validation_error' ? nonFieldMessage : undefined) ??
    (apiCode !== undefined ? translateErrorCode(apiCode) : undefined) ??
    detail ??
    translateErrorCode(code) ??
    translateErrorCode(fallbackCode(status)) ??
    '';
  return result;
}

/** Anything with Mantine-form-like `setFieldError` (a `useForm()` result or a tiny local adapter). */
export interface FieldErrorTarget {
  setFieldError: (path: string, error: ReactNode) => void;
}

export interface ShowApiErrorOptions {
  /** Form to receive field errors. Without it every error becomes a notification. */
  form?: FieldErrorTarget;
  /** Fields the form actually renders; errors for other fields fall back to the notification. */
  fields?: readonly string[];
  /** Notification title (defaults to «Ошибка»). */
  title?: ReactNode;
}

/** Shows a parsed error: field errors into the form when possible, the rest as one notification. */
export function showApiError(error: unknown, options: ShowApiErrorOptions = {}): ParsedApiError {
  const parsed = parseApiError(error);
  if (parsed.code === 'canceled') return parsed;

  const { form, fields } = options;
  const leftovers: string[] = [];
  let shownInForm = 0;
  for (const [field, message] of Object.entries(parsed.fieldErrors)) {
    if (form && (!fields || fields.includes(field))) {
      form.setFieldError(field, message);
      shownInForm += 1;
    } else {
      // Without a visible input the bare text («Обязательное поле») is ambiguous: name the field.
      leftovers.push(`${field}: ${message}`);
    }
  }

  const isPlainValidation = parsed.code === 'validation_error' && shownInForm > 0;
  const notificationText = [isPlainValidation ? '' : parsed.message, ...leftovers]
    .filter((text, index, all) => text && all.indexOf(text) === index)
    .join('\n');
  if (notificationText) {
    notifications.show({
      color: 'red',
      title: options.title ?? i18n.t('common:notify.errorTitle'),
      message: notificationText,
      style: { whiteSpace: 'pre-line' },
    });
  }
  return parsed;
}

/** Hook form of `showApiError` for mutations: `onError: (error) => handleError(error, {form})`. */
export function useApiError(): (error: unknown, options?: ShowApiErrorOptions) => ParsedApiError {
  return useCallback((error: unknown, options?: ShowApiErrorOptions) => showApiError(error, options), []);
}

/** `invalid_status` of a decision: the card was already decided (or changed) by another staff member. */
export function isStaleDecision(error: unknown): boolean {
  return parseApiError(error).code === 'invalid_status';
}

/**
 * For the catch of a decision dialog (approve, reject, close, cancel, block…): when the card is
 * stale, tells the staff so and returns `true` — the caller then lets the dialog close (the
 * mutation's `onError` has already refreshed the card). `false`: report the error as usual.
 */
export function notifyIfStaleDecision(error: unknown): boolean {
  if (!isStaleDecision(error)) return false;
  notifications.show({
    color: 'gray',
    title: i18n.t('common:notify.alreadyDecidedTitle'),
    message: i18n.t('common:notify.alreadyDecided'),
  });
  return true;
}
